<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import {
  listProjectTrainings,
  cancelProjectTraining,
} from '@/api/RobVisionApi';
import {
  type VisionProject,
  type ProjectTrainingRun,
  TrainingStatus,
  ACTIVE_TRAINING_STATUSES,
  DeploymentStatus,
} from '@/models/RobVision';
import formatDate from '@/helpers/DateFormatterHelper';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();

const props = defineProps<{
  project: VisionProject;
  isArchived: boolean;
  // Whether a (re)training may be started right now — owned by the view.
  trainingAllowed: boolean;
}>();

const emit = defineEmits<{
  // The view owns the confirm-training and deployment dialogs. deploy
  // without a target = newest successful run; with a target = that specific
  // run (isRollback marks a non-newest pick for the dialog's warning).
  retrain: [];
  deploy: [target?: { runId: string; isRollback: boolean }];
  // Header "Train New Model Version": the view switches to the Labeling tab
  // and opens the fine-tune wizard there — one training flow everywhere.
  'train-new': [];
  'training-triggered': [];
}>();

const runs = ref<ProjectTrainingRun[]>([]);
const loaded = ref(false);
const stopDialogOpen = ref(false);
const stopError = ref('');
const stopping = ref(false);

let pollTimer: ReturnType<typeof setInterval> | null = null;

// Deployments still in flight — the row shows their progress and the table
// keeps polling until they settle.
const ACTIVE_DEPLOYMENT_STATUSES = [
  DeploymentStatus.Starting,
  DeploymentStatus.Deploying,
];

interface ModelRow {
  run: ProjectTrainingRun;
  name: string;
  base: boolean;
  lineageKnown: boolean;
  version: string;
  statusLabel: string;
  statusColor: string;
  inTraining: boolean;
  dateText: string;
  canDeploy: boolean;
  isRollback: boolean;
  canRetrain: boolean;
  canStop: boolean;
}

const rows = computed<ModelRow[]>(() => {
  const newestSucceededId = runs.value.find(
    (run) => run.status === TrainingStatus.SUCCEEDED,
  )?.runId;
  return runs.value.map((run) => {
    // Only claim lineage the backend actually stamped — guessing (e.g.
    // "oldest run = base") mislabels fine-tuned runs recorded before the
    // stamp existed. Unknown lineage shows no badge.
    const base = run.baseModel === true;
    return {
      run,
      base,
      lineageKnown: run.baseModel != null,
      // A custom run name (given at trigger time) wins over the generated one.
      name:
        run.name ||
        (base
          ? $gettext('%{ name } base', { name: props.project.name })
          : run.baseModel === false
            ? $gettext('%{ name } refined', { name: props.project.name })
            : props.project.name),
      // Only number what the backend numbered (successful models get a
      // sequential version) — an invented list-position "version" collides
      // with the real ones and numbers failed runs that produced no model.
      version: run.modelVersion != null ? `V${run.modelVersion}` : '—',
      ...statusInfo(run),
      dateText: run.createdAt ? formatDate(run.createdAt, true) : '—',
      // Every successful run stays deployable (rollback); the guard against
      // a fallback row without runId keeps deploy off broken rows.
      canDeploy:
        run.status === TrainingStatus.SUCCEEDED &&
        !!run.runId &&
        !props.isArchived,
      isRollback:
        newestSucceededId !== undefined && run.runId !== newestSucceededId,
      canRetrain:
        base &&
        props.trainingAllowed &&
        !props.isArchived &&
        !anyInTraining.value,
      // Archived projects can still stop a run started before archiving;
      // CANCELING stays stoppable — a failed termination is simply retried.
      canStop: ACTIVE_TRAINING_STATUSES.includes(run.status),
    };
  });
});

function statusInfo(run: ProjectTrainingRun): {
  statusLabel: string;
  statusColor: string;
  inTraining: boolean;
} {
  switch (run.status) {
    case TrainingStatus.STARTING:
    case TrainingStatus.RUNNING:
      return {
        statusLabel: $gettext('In training'),
        statusColor: 'info',
        inTraining: true,
      };
    case TrainingStatus.SUCCEEDED:
      // Aggregate over the run's deployments: any in flight → deploying;
      // otherwise deployed as soon as one CU runs it; all failed/timed out
      // → surface that instead of a false "Deployed".
      if (
        run.deployments.some((deployment) =>
          ACTIVE_DEPLOYMENT_STATUSES.includes(deployment.status),
        )
      ) {
        return {
          statusLabel: $gettext('Deploying…'),
          statusColor: 'info',
          inTraining: false,
        };
      }
      if (
        run.deployments.some(
          (deployment) => deployment.status === DeploymentStatus.Success,
        )
      ) {
        return {
          statusLabel: $gettext('Deployed'),
          statusColor: 'success',
          inTraining: false,
        };
      }
      if (run.deployments.length > 0) {
        return {
          statusLabel: $gettext('Deployment failed'),
          statusColor: 'warning',
          inTraining: false,
        };
      }
      return {
        statusLabel: $gettext('Trained'),
        statusColor: 'success',
        inTraining: false,
      };
    case TrainingStatus.CANCELING:
      return {
        statusLabel: $gettext('Canceling…'),
        statusColor: 'grey',
        inTraining: true,
      };
    case TrainingStatus.FAILED:
      return {
        statusLabel: $gettext('Training failed'),
        statusColor: 'error',
        inTraining: false,
      };
    case TrainingStatus.CANCELED:
      return {
        statusLabel: $gettext('Canceled'),
        statusColor: 'grey',
        inTraining: false,
      };
    default:
      return {
        statusLabel: $gettext('Unknown'),
        statusColor: 'grey',
        inTraining: false,
      };
  }
}

function deploymentChip(status: DeploymentStatus): {
  color?: string;
  icon: string;
} {
  if (status === DeploymentStatus.Success) {
    return { color: 'success', icon: 'mdi-check-circle-outline' };
  }
  if (ACTIVE_DEPLOYMENT_STATUSES.includes(status)) {
    return { icon: 'mdi-progress-clock' };
  }
  return { color: 'error', icon: 'mdi-alert-circle-outline' };
}

const headers = computed(() => [
  { title: $gettext('Name'), key: 'name', sortable: false },
  { title: $gettext('Version'), key: 'version', sortable: false },
  { title: $gettext('Status'), key: 'status', sortable: false },
  { title: $gettext('Deployed on'), key: 'deployedOn', sortable: false },
  { title: $gettext('Date'), key: 'date', sortable: false },
  { title: '', key: 'actions', sortable: false, align: 'end' as const },
]);

const latestSucceeded = computed(() =>
  runs.value.some((run) => run.status === TrainingStatus.SUCCEEDED),
);
const anyInTraining = computed(() =>
  runs.value.some((run) => ACTIVE_TRAINING_STATUSES.includes(run.status)),
);
const anyDeploying = computed(() =>
  runs.value.some((run) =>
    run.deployments.some((deployment) =>
      ACTIVE_DEPLOYMENT_STATUSES.includes(deployment.status),
    ),
  ),
);
// The table stays live while either a training or a deployment is running.
const pollNeeded = computed(() => anyInTraining.value || anyDeploying.value);

async function refresh() {
  try {
    runs.value = await listProjectTrainings(props.project.uuid);
  } catch (error) {
    console.error((error as Error).message);
  } finally {
    loaded.value = true;
  }
}

function openStopDialog() {
  stopError.value = '';
  stopDialogOpen.value = true;
}

async function stopTraining() {
  if (stopping.value) {
    return;
  }
  stopping.value = true;
  stopError.value = '';
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await cancelProjectTraining(props.project.uuid);
    stopDialogOpen.value = false;
    refresh();
    emit('training-triggered');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    stopError.value =
      error?.message || $gettext('The training could not be stopped');
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
    stopping.value = false;
  }
}

// Keep the table live while a run is active (same cadence as the view's
// own status poll).
watch(
  pollNeeded,
  (active) => {
    if (active && !pollTimer) {
      pollTimer = setInterval(refresh, 15_000);
    } else if (!active && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },
  { immediate: true },
);

onMounted(() => {
  refresh();
});

onBeforeUnmount(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
  }
});

defineExpose({ refresh });
</script>

<template>
  <v-row>
    <v-col cols="12" class="d-flex align-end justify-space-between ga-2">
      <div class="section-intro">
        <h3>{{ $gettext('Model') }}</h3>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{
            $gettext(
              'Track all model versions, see where they run and deploy them to robots.',
            )
          }}
        </p>
      </div>
      <div class="d-flex align-center ga-2 section-actions">
        <r-btn
          variant="outlined"
          icon="mdi-auto-fix"
          :disabled="isArchived || anyInTraining"
          @click="emit('train-new')"
        >
          {{ $gettext('Train New Model Version') }}
        </r-btn>
        <r-btn
          variant="accent"
          icon="mdi-robot-industrial"
          :disabled="!latestSucceeded || isArchived"
          @click="emit('deploy')"
        >
          {{ $gettext('Deploy to Robot') }}
        </r-btn>
      </div>
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="12">
      <v-card v-if="loaded && rows.length === 0" rounded="lg" class="pa-8">
        <p class="text-body-1 mb-1">
          {{ $gettext('No trained models yet.') }}
        </p>
        <p class="text-body-2 text-medium-emphasis mb-4">
          {{
            $gettext(
              'Train the base model from your part definition — refined versions with labeled real images follow later.',
            )
          }}
        </p>
        <r-btn
          v-if="trainingAllowed"
          variant="accent"
          icon="mdi-clock-start"
          :disabled="isArchived"
          @click="emit('retrain')"
        >
          {{ $gettext('Start Base Model Training') }}
        </r-btn>
      </v-card>
      <v-card v-else rounded="lg">
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="!loaded"
          items-per-page="-1"
          density="comfortable"
        >
          <template #item.name="{ item }">
            <span class="font-weight-medium mr-2">{{ item.name }}</span>
            <v-chip
              v-if="item.lineageKnown"
              size="x-small"
              :color="item.base ? 'info' : 'warning'"
              density="comfortable"
            >
              {{
                item.base
                  ? $gettext('Base model')
                  : $gettext('Refined with images')
              }}
            </v-chip>
          </template>
          <template #item.version="{ item }">
            <span class="font-weight-medium">{{ item.version }}</span>
          </template>
          <template #item.status="{ item }">
            <span class="d-inline-flex align-center">
              <v-progress-circular
                v-if="item.inTraining"
                indeterminate
                size="12"
                width="2"
                class="mr-2"
              />
              <v-icon
                v-else
                icon="mdi-square-rounded"
                size="x-small"
                :color="item.statusColor"
                class="mr-2"
              />
              {{ item.statusLabel }}
            </span>
          </template>
          <template #item.deployedOn="{ item }">
            <template v-if="item.run.deployments.length > 0">
              <!-- A chip only reads as "the model runs here" when it is
                   green — in-flight and failed deployments are visually
                   distinct so a failed CU never looks deployed. -->
              <v-tooltip
                v-for="deployment in item.run.deployments"
                :key="deployment.serialNumber"
                location="bottom"
              >
                <template v-slot:activator="{ props: tooltipProps }">
                  <v-chip
                    v-bind="tooltipProps"
                    size="small"
                    density="comfortable"
                    class="mr-1"
                    :color="deploymentChip(deployment.status).color"
                    :prepend-icon="deploymentChip(deployment.status).icon"
                  >
                    {{ deployment.serialNumber }}
                  </v-chip>
                </template>
                <span>{{ deployment.status }}</span>
              </v-tooltip>
            </template>
            <span v-else class="text-medium-emphasis">—</span>
          </template>
          <template #item.date="{ item }">
            <span class="text-body-2">{{ item.dateText }}</span>
          </template>
          <template #item.actions="{ item }">
            <v-menu
              v-if="item.canDeploy || item.canRetrain || item.canStop"
              location="bottom end"
            >
              <template v-slot:activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  icon="mdi-dots-vertical"
                  variant="text"
                  size="small"
                  :aria-label="$gettext('Model actions')"
                />
              </template>
              <v-list density="compact">
                <v-list-item
                  v-if="item.canDeploy"
                  prepend-icon="mdi-robot-industrial"
                  :title="$gettext('Deploy to Robot')"
                  @click="
                    emit('deploy', {
                      runId: item.run.runId,
                      isRollback: item.isRollback,
                    })
                  "
                />
                <v-list-item
                  v-if="item.canRetrain"
                  prepend-icon="mdi-clock-start"
                  :title="$gettext('Retrain Base Model')"
                  @click="emit('retrain')"
                />
                <v-list-item
                  v-if="item.canStop"
                  prepend-icon="mdi-stop-circle-outline"
                  :title="$gettext('Stop Training')"
                  class="text-error"
                  @click="openStopDialog"
                />
              </v-list>
            </v-menu>
          </template>
          <template #bottom></template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>

  <!-- Stop-training confirmation -->
  <v-dialog v-model="stopDialogOpen" max-width="480">
    <v-card rounded="lg">
      <v-card-title>{{ $gettext('Stop Training?') }}</v-card-title>
      <v-card-text>
        <p class="mb-0">
          {{
            $gettext(
              'The running training will be terminated and marked as canceled. This cannot be undone — a new training has to start from scratch.',
            )
          }}
        </p>
        <p v-if="stopError" class="text-error text-body-2 mt-2 mb-0">
          {{ stopError }}
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <r-btn variant="outlined" @click="stopDialogOpen = false">
          {{ $gettext('Keep Training') }}
        </r-btn>
        <r-btn
          variant="accent"
          icon="mdi-stop-circle-outline"
          :disabled="stopping"
          @click="stopTraining"
        >
          {{ $gettext('Stop Training') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* The button group must sit at the same top-right spot in every language:
   it never shrinks or re-arranges — the intro column absorbs the squeeze
   and wraps its text instead (German labels run ~40% longer). */
.section-intro {
  flex: 1 1 auto;
  min-width: 120px;
}
.section-actions {
  flex-shrink: 0;
}

/* Dataset choices as bordered rows (design language of the roll-out dialog);
   also evens out the radio group's uneven left inset in the dialog. */
.dataset-option {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 8px 12px;
}

.dataset-option + .dataset-option {
  margin-top: 8px;
}
</style>
